// CLOUDWEB-20913: postMessage-мост SW <-> page для открытия нативного приложения по
// cloudmailru:// при клике по PWA-пушу. Живёт отдельным модулем: с жизненным циклом
// подписки (webPush.ts) не связан, а изоляция стока window.open/location.href и
// валидатора доверенного url упрощает их точечное тестирование.
import { webpushDebug } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import { isInStandaloneMode } from 'reactApp/appHelpers/standaloneHelpers';
import { sendXray } from 'reactApp/utils/ga';
import { type DeeplinkReplyMessage, DEEPLINK_REPLY_TYPE, DEEPLINK_REQUEST_TYPE } from 'serviceWorker/deeplinkBridge';

import { FEATURE_NAME, log, XRAY_DEEPLINK } from './shared';

// Монотонный seq: разбивает записи, попавшие в одну миллисекунду, и держит
// внутриконтекстный порядок (ключ сортировки ленты — ts+seq). Снимаем синхронно,
// в порядке вызовов, поэтому seq отражает порядок событий.
let debugSeq = 0;

function webPushDebugLog(entry: Record<string, unknown>) {
    if (!webpushDebug) {
        return;
    }

    const seq = debugSeq++;

    try {
        const req = indexedDB.open('webpush-debug-page', 1);
        req.onupgradeneeded = () => {
            req.result.createObjectStore('logs', { autoIncrement: true });
        };
        req.onsuccess = () => {
            try {
                const db = req.result;
                const tx = db.transaction('logs', 'readwrite');
                // ts берём из entry (снят в dbgLog в момент события), а НЕ Date.now() здесь:
                // onsuccess срабатывает после async-open, его переменная латентность сдвинула бы метку.
                tx.objectStore('logs').add({ seq, ...entry });
                tx.oncomplete = () => db.close();
                tx.onerror = () => db.close();
            } catch (_) {}
        };
        req.onerror = () => {};
    } catch (_) {}
}

// CLOUDWEB-20913: rawUrl из payload пуша (app_url) — данные, пересекающие границу доверия.
// app_url приходит уже как cloudmailru:// (нативная схема) либо как https:// (тогда его
// конвертит openDeeplink). Пропускаем обе схемы + домен облака: apex (mail.ru / mini-mail.ru)
// и любой их поддомен. Иначе: javascript:/data: → XSS, http:/чужой хост → open-redirect и
// инъекция cloudmailru://<чужой-хост> в нативное приложение.
const TRUSTED_DEEPLINK_HOSTS = ['mail.ru', 'mini-mail.ru'];

export function isTrustedDeeplinkUrl(rawUrl: string): boolean {
    try {
        const { protocol, hostname } = new URL(rawUrl);

        const schemeOk = protocol === 'https:' || protocol === 'cloudmailru:';
        const hostOk = TRUSTED_DEEPLINK_HOSTS.some((base) => hostname === base || hostname.endsWith(`.${base}`));

        return schemeOk && hostOk;
    } catch {
        return false;
    }
}

// Заменяет прежний IDB-поллинг: страница сообщает SW о готовности (REQUEST),
// SW отдаёт удержанный в памяти диплинк (REPLY).
export function initDeeplinkBridge() {
    if (!('serviceWorker' in navigator) || !isInStandaloneMode()) {
        return;
    }

    const dbgLog = (event: string, extra: Record<string, unknown> = {}) => {
        // timestamp снимаем здесь, в момент события (не в onsuccess IDB): метка не зависит от латентности open, а консольная строка и IDB-запись делят один инстант.
        const ts = Date.now();
        log('[deeplink-bridge]', event, extra);
        webPushDebugLog({ ts, event, ...extra });
    };

    // Дедуп против двойного открытия: SW отвечает и на broadcast (уже открытый PWA),
    // и на собственный REQUEST страницы — в узком окне это может прийти дважды.
    let lastUrl: string | null = null;
    let lastUrlTs = 0;
    const DEDUP_WINDOW_MS = 2000;

    const openDeeplink = (rawUrl: string) => {
        // Валидируем ДО дедупа, чтобы недоверенный url не занимал слот дедупа.
        if (!isTrustedDeeplinkUrl(rawUrl)) {
            dbgLog('deeplink rejected: untrusted url', { rawUrl });
            sendXray([FEATURE_NAME, XRAY_DEEPLINK, 'rejected']);
            return;
        }

        const now = Date.now();
        if (rawUrl === lastUrl && now - lastUrlTs < DEDUP_WINDOW_MS) {
            dbgLog('deeplink dedup skip', { rawUrl });
            return;
        }
        lastUrl = rawUrl;
        lastUrlTs = now;

        const appUrl = rawUrl.replace(/^https:\/\//, 'cloudmailru://');
        dbgLog('opening native app', { appUrl, rawUrl });
        try {
            const opened = window.open(appUrl);
            dbgLog('window.open result', { opened: !!opened });
            if (opened) {
                sendXray([FEATURE_NAME, XRAY_DEEPLINK, 'open']);
            } else {
                sendXray([FEATURE_NAME, XRAY_DEEPLINK, 'fallback']);
                window.location.href = appUrl;
                dbgLog('fallback to location.href', { appUrl });
            }
        } catch (error) {
            dbgLog('window.open threw, falling back to location.href', { error: String(error) });
            try {
                sendXray([FEATURE_NAME, XRAY_DEEPLINK, 'fallback']);
                window.location.href = appUrl;
                dbgLog('fallback to location.href', { appUrl });
            } catch (_) {}
        }
    };

    // eslint-disable-next-line compat/compat
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as Partial<DeeplinkReplyMessage> | undefined;
        if (!data || data.type !== DEEPLINK_REPLY_TYPE) {
            return;
        }
        dbgLog('message reply received', { hasAppUrl: !!data.appUrl });
        if (typeof data.appUrl === 'string' && data.appUrl) {
            openDeeplink(data.appUrl);
        }
    });

    const sendRequest = (reason: string) => {
        // eslint-disable-next-line compat/compat
        navigator.serviceWorker.ready
            .then((registration) => {
                dbgLog('sending request', { reason, hasActive: !!registration.active });
                registration.active?.postMessage({ type: DEEPLINK_REQUEST_TYPE });
            })
            .catch((error) => {
                dbgLog('serviceWorker.ready rejected', { reason, error: String(error) });
            });
    };

    dbgLog('bridge init');
    sendRequest('init');
    // controllerchange: новый SW взял управление (например, после skipWaiting) —
    // повторяем REQUEST, т.к. предыдущий active мог смениться.
    // eslint-disable-next-line compat/compat
    navigator.serviceWorker.addEventListener('controllerchange', () => sendRequest('controllerchange'));
}
